"use client"

import { AppRouterCacheProvider } from '@mui/material-nextjs/v14-appRouter';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from '@/theme';
import { SnackbarKey, SnackbarProvider, useSnackbar } from 'notistack';
import { InfoFiadoProvider } from '@/context/infoFiadoContext';
import { SessionProvider, signIn, signOut, useSession } from "next-auth/react";
import './globals.css';
import { CurrentSessionProvider } from '@/context/currentSessionContext';
import { initializeLogCollector } from '@/utils/logCollector';
import IconButton from '@mui/material/IconButton';
import { EmissionProvider } from '@/context/emissionContext';
import { useEffect, useState, useRef } from 'react';
import ModalProvider from 'mui-modal-provider';
import { CustomSnackbarProvider } from '@/components/providers/CustomSnackbarProvider';
import { ExpedienteContext, ExpedienteProvider } from '@/context/expedienteContext';
import { DetailPolicyProvider } from '@/context/detailPolicyContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { QueryPersistProvider } from '@/components/providers/QueryPersistProvider';
import dynamic from 'next/dynamic';
import { NavigationProvider } from '@/context/navigationContext';

// Lazy load del chat para mejorar tiempo de carga inicial
const AtomChatWrapper = dynamic(() => import('@/components/chat/AtomChatWrapper'), {
  ssr: false,
  loading: () => null
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos
      retry: 2, // Reducir reintentos para carga más rápida
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false, // Evitar refetch innecesarios
      refetchOnMount: false, // Evitar refetch si hay datos en cache
    },
  },
});

// // Variable global para controlar la carga del script
// let isScriptLoaded = false;

export default function RootLayout(props: { children: React.ReactNode }) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const scriptAppended = useRef(false);

  useEffect(() => {
    const src = "https://atom-plugin-io.web.app/atom-plugin-web.js";

    // Verificar si ya existe
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`
    );

    if (existingScript) {
      console.log("Script atom-plugin ya está cargado");
      setScriptLoaded(true);
      return;
    }

    if (scriptAppended.current) return; // evita duplicados

    console.log("Cargando script atom-plugin por primera vez...");
    const script = document.createElement("script");
    script.src = src;
    script.async = true;

    script.onload = () => {
      console.log("Script atom-plugin cargado exitosamente");
      setScriptLoaded(true);
    };

    script.onerror = () => {
      console.error("Error al cargar script atom-plugin");
    };

    document.body.appendChild(script);
    scriptAppended.current = true;

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, []);

  // Efecto para personalizar el shadowRoot del plugin
  useEffect(() => {
    if (!scriptLoaded) return;

    const checkChatLoaded = setInterval(() => {
      const atomPlugin = document.querySelector("atom-plugin-web");
      if (atomPlugin && (atomPlugin as any).shadowRoot) {
        const style = document.createElement("style");
        style.innerHTML = `
          .messenger-button { visibility: hidden; }
          .button-display { margin-bottom: 135px; margin-left:160px; }
        `;
        (atomPlugin as any).shadowRoot.appendChild(style);
        clearInterval(checkChatLoaded);
      }
    }, 500);

    return () => clearInterval(checkChatLoaded);
  }, [scriptLoaded]);

  return (

    <html lang="en">
      <body>
        {scriptLoaded && (
          /*<atom-plugin-web
            id="luv3Ki0btuTL6wgjlnp7"
            company="oiIR8mRAAlkNLkgp40I4"
            style={{ marginBottom: '150px' }}
            customchat='["position: bottom-left", "margin-bottom: 150px"]'
          />*/
          <AtomChatWrapper />
        )}
        <QueryClientProvider client={queryClient}>
          <QueryPersistProvider options={{
            storageType: 'localStorage',
            debug: process.env.NODE_ENV === 'development',
            maxAge: 1000 * 60 * 60 * 1 // 1 hora
          }}>
            <CurrentSessionProvider >
              <AppRouterCacheProvider options={{ enableCssLayer: true }}>
                <ThemeProvider theme={theme}>
                  <InfoFiadoProvider>
                    <SessionProvider>
                      <EmissionProvider>
                        <ExpedienteProvider>
                          <DetailPolicyProvider>
                            {/* <CssBaseline /> */}
                            <CssBaseline />
                            <CustomSnackbarProvider >
                              {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                              <ModalProvider>
                                <NavigationProvider>
                                  {props.children}
                                </NavigationProvider>
                              </ModalProvider>
                            </CustomSnackbarProvider>
                          </DetailPolicyProvider>
                        </ExpedienteProvider>
                      </EmissionProvider>
                    </SessionProvider>
                  </InfoFiadoProvider>
                </ThemeProvider>
              </AppRouterCacheProvider>
            </CurrentSessionProvider>
          </QueryPersistProvider>
        </QueryClientProvider>


      </body>
    </html>
  );
}
